import React from "react";

export default function FintrustAIAbout() {
  return (
    <div className="min-h-screen bg-black text-gray-300 px-4 py-6 leading-relaxed">
      
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-extrabold text-green-400">FinTrust AI</h1>
        <p className="text-lg text-gray-400 mt-1">
          AI-Powered Financial Trust & Cybersecurity
        </p>
        <p className="text-sm text-blue-400 mt-1">
          Powered by Think5Whys Global DeepTech Solutions
        </p>
      </div>

      {/* Section */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-yellow-300 mb-3">
          Adopt Zero Trust Architecture
        </h2>
        <p className="text-gray-400">
          FinTrust AI is an AI-driven cybersecurity solution built for BFSI and
          other sensitive sectors. It uses machine learning, anomaly detection,
          and behavioral analytics to detect cyber threats, fraud risks, and
          regulatory compliance issues — in real time.
        </p>
      </section>

      {/* Background */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-green-400 mb-3">Background</h2>
        <p className="text-gray-400 mb-4">
          Cybersecurity in BFSI and high-risk sectors like eCommerce, IoT, and
          public infrastructure is extremely critical. BFSI organizations handle
          sensitive personal and financial data, making them prime targets for
          cyberattacks.
        </p>

        <div className="space-y-3 text-gray-400 text-sm">
          <p>1. <b>High Value of Data:</b> Massive financial & personal data attracts cybercriminals.</p>
          <p>2. <b>Regulatory Compliance:</b> GDPR, PCI-DSS, HIPAA demand strict protection.</p>
          <p>3. <b>Increasing Cyber Threats:</b> Ransomware, APTs, phishing, malware.</p>
          <p>4. <b>Digital Transformation:</b> Mobile banking & cloud adoption increase attack surfaces.</p>
          <p>5. <b>Customer Trust:</b> A single breach destroys long-term trust.</p>
          <p>6. <b>Financial Impact:</b> Direct theft + legal penalties + recovery cost.</p>
          <p>7. <b>Third-Party Risks:</b> Vendor integrations introduce new vulnerabilities.</p>
          <p>8. <b>Zero Trust:</b> Continuous verification is now a global standard.</p>
        </div>
      </section>

      {/* Key Solutions */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-purple-400 mb-3">
          Key Cybersecurity Solutions in Demand
        </h2>
        <ul className="list-disc ml-5 text-gray-400 space-y-2 text-sm">
          <li>Endpoint Protection</li>
          <li>Network Security (IDS/IPS, Firewalls)</li>
          <li>Data Encryption</li>
          <li>Identity & Access Management (IAM)</li>
          <li>Threat Intelligence</li>
          <li>Cloud Security</li>
          <li>Incident Response</li>
          <li>AI/ML Threat Detection</li>
        </ul>
      </section>

      {/* Features */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-cyan-400 mb-3">
          FinTrust AI — Key Features & Benefits
        </h2>
        <div className="space-y-3 text-sm text-gray-400">

          <div>
            <p className="text-green-400 font-semibold">AI-Powered Threat Detection</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Real-time detection of DDoS, malware, brute-force attacks</li>
              <li>Machine learning based anomaly detection</li>
              <li>Early alerts for fraud & phishing</li>
            </ul>
          </div>

          <div>
            <p className="text-green-400 font-semibold">BFSI-Specific Protection</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Monitors transactions & customer data patterns</li>
              <li>Prevents unauthorized access</li>
              <li>Detects insider threats</li>
            </ul>
          </div>

          <div>
            <p className="text-green-400 font-semibold">Real-Time Monitoring & Alerts</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Live threat monitoring dashboard</li>
              <li>Email/SMS alerts for critical events</li>
              <li>Firewall + IT infra integration</li>
            </ul>
          </div>

          <div>
            <p className="text-green-400 font-semibold">Easy, Affordable & Flexible</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Runs on-prem or cloud (AWS, GCP, Azure)</li>
              <li>Plug & play with existing banking systems</li>
              <li>Pricing starts at <b>$499</b></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-orange-400 mb-3">
          Who Needs This?
        </h2>
        <ul className="list-disc ml-5 text-gray-400 space-y-2 text-sm">
          <li>Small & Mid-Sized Banks</li>
          <li>Fintech Startups</li>
          <li>Cybersecurity Providers</li>
          <li>Insurance & Payment Gateways</li>
        </ul>
      </section>

      {/* Other Sectors */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-blue-400 mb-3">
          Other Industries Using FinTrust AI
        </h2>
        <ul className="list-disc ml-5 text-gray-400 space-y-1 text-sm">
          <li>Government, Defence, Smart Cities</li>
          <li>Healthcare & Life Sciences</li>
          <li>Retail & E-Commerce</li>
          <li>Telecom & Internet</li>
          <li>Energy & Utilities</li>
          <li>Travel & Hospitality</li>
          <li>Logistics & Supply Chain</li>
          <li>Real Estate & Property</li>
          <li>Education & EdTech</li>
          <li>Media & Entertainment</li>
        </ul>
      </section>

      {/* Competitive Table */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-pink-400 mb-4">
          Competitive Advantage
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full border border-gray-700 text-sm">
            <thead className="bg-gray-800 text-gray-200">
              <tr>
                <th className="border px-3 py-2">Feature</th>
                <th className="border px-3 py-2">Our System ($499)</th>
                <th className="border px-3 py-2">Enterprise ($1000+)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border px-3 py-2">AI Threat Detection</td>
                <td className="border px-3 py-2 text-green-400">Yes</td>
                <td className="border px-3 py-2 text-green-400">Yes</td>
              </tr>
              <tr>
                <td className="border px-3 py-2">BFSI-Specific</td>
                <td className="border px-3 py-2 text-green-400">Yes</td>
                <td className="border px-3 py-2 text-red-400">No</td>
              </tr>
              <tr>
                <td className="border px-3 py-2">Cloud & On-Prem</td>
                <td className="border px-3 py-2 text-green-400">Yes</td>
                <td className="border px-3 py-2 text-red-400">No</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Deliverables */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-green-300 mb-3">
          Deliverables — FinTrust AI Cybersecurity Bundle
        </h2>

        <div className="space-y-4 text-sm text-gray-400">
          <div>
            <p className="font-semibold text-yellow-300">1. Business & Sales Documents</p>
            <ul className="list-disc ml-5">
              <li>Business Proposition</li>
              <li>Pitch Deck</li>
              <li>Sales Brochure</li>
              <li>Proposal Template</li>
              <li>Licensing Contract</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-yellow-300">
              2. Technical & Product Docs
            </p>
            <ul className="list-disc ml-5">
              <li>Architecture & AI Model Docs</li>
              <li>Requirements Document</li>
              <li>API Documentation</li>
              <li>Installation Guide</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-yellow-300">
              3. Deployment & Compliance
            </p>
            <ul className="list-disc ml-5">
              <li>Deployment Guide</li>
              <li>Compliance Report</li>
              <li>Disaster Recovery Plan</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-yellow-300">
              4. Customer Support & Training
            </p>
            <ul className="list-disc ml-5">
              <li>User Manual</li>
              <li>Training Material</li>
              <li>Support SLA</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Deployment Strategy */}
      <section className="mb-14">
        <h2 className="text-xl font-bold text-teal-400 mb-3">
          Selling & Deploying FinTrust AI (On-Premise)
        </h2>

        <div className="space-y-5 text-gray-400 text-sm">
          <div>
            <p className="font-semibold text-blue-300">1. Sales Strategy</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Target CIOs, CISOs, CTOs</li>
              <li>Direct Enterprise Sales</li>
              <li>Channel Partner Network</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-blue-300">2. Deployment Steps</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Pre-deployment audit</li>
              <li>Server provisioning</li>
              <li>Firewall & IAM integration</li>
              <li>Testing & Validation</li>
              <li>Training & Handover</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-blue-300">3. Post-Deployment</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>AI model updates</li>
              <li>24/7 technical support</li>
              <li>Regulatory audit assistance</li>
            </ul>
          </div>
        </div>
      </section>

    </div>
  );
}
